import { useCallback } from "react"
import type { PropsWithChildren, ReactNode } from "react"
import { useMessagingView } from "../hooks"
import { classNames } from "../utils"
import styles from "./Menu.module.scss"

export enum MenuItemColor {
    Red = "red",
}

type MenuItemProps = {
    /**
     * Icon to display in the menu item
     */
    icon?: ReactNode
    /**
     * Text to display in the menu item
     */
    text?: string
    /**
     * Callback to execute when the menu item is clicked
     */
    onClick?: () => void
    /**
     * Color variant of the menu item
     */
    color?: MenuItemColor
    /**
     * Additional class names for custom styling
     */
    className?: string
    /**
     * Data test ID for testing
     */
    "data-testid"?: string
}

export function MenuItem({
    children,
    icon,
    text,
    onClick,
    color,
    className,
    ...props
}: PropsWithChildren<MenuItemProps & React.HTMLAttributes<HTMLDivElement>>) {
    const messagingView = useMessagingView()
    const isDesktop = !messagingView.isMobile
    const handleClick = useCallback(() => {
        if (onClick) {
            onClick()
        }
    }, [onClick])

    const menuItemClassName = classNames(
        styles.menuItem,
        {
            [styles.red]: color === MenuItemColor.Red,
            [styles.desktop]: isDesktop,
        },
        className,
    )

    return (
        <div className={menuItemClassName} onClick={handleClick} {...props}>
            {icon}
            {text && <span>{text}</span>}
            {children}
        </div>
    )
}
